import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ChartData, ChartOptions, ChartType } from 'chart.js';
import { Moment } from 'moment';
import { BaseChartDirective } from 'ng2-charts';
import { NgxSpinnerService, Spinner } from 'ngx-spinner';
import { debounceTime, distinctUntilChanged, finalize, map, Observable, of, startWith, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { StatisticsService } from 'src/app/data/statistics.service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { EmployerObjectDictionaryDto } from 'src/app/models/dtos/employer-object-dictionary-dto';
import { WorkerAgreementDurationStatsDto } from 'src/app/models/dtos/statistics/worker-agreement/worker-agreement-duration-stats-dto';

@Component({
  selector: 'app-statistics-worker-agreements',
  templateUrl: './statistics-worker-agreements.component.html',
  styleUrl: './statistics-worker-agreements.component.scss',
  standalone: false
})
export class StatisticsWorkerAgreementsComponent implements OnInit, OnDestroy {
  private dictionaryService = inject(DictionaryService);
  private formBuilder = inject(UntypedFormBuilder);
  private statisticsService = inject(StatisticsService);
  private spinner = inject(NgxSpinnerService);
  private translateService = inject(TranslateService);
  private router = inject(Router);

  @ViewChild('employerObjectInput', { read: MatAutocompleteTrigger }) employerObjectSelect: MatAutocompleteTrigger;
  @ViewChild('employerObjectInput') protected employerObjectSelectRef: ElementRef;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  statisticsWorkerAgreementsFormGroup: UntypedFormGroup;
  listOfEmployers$: Observable<DictionaryItem[]> = this.dictionaryService.getEmployersList();
  listOfEmployerObjects$: Observable<EmployerObjectDictionaryDto[]> = null;

  private readonly timeBetweenInput = 300;
  private readonly unsubscribe$ = new Subject<void>();

  concludedTotal: number = null;
  terminatedTotal: number = null;

  constructor() { }

  get employer() {
    return this.statisticsWorkerAgreementsFormGroup.get('employer') as UntypedFormControl;
  }
  get employerObject() {
    return this.statisticsWorkerAgreementsFormGroup.get('employerObject') as UntypedFormControl;
  }
  get dateFrom() {
    return this.statisticsWorkerAgreementsFormGroup.get('dateFrom') as UntypedFormControl;
  }
  get dateTo() {
    return this.statisticsWorkerAgreementsFormGroup.get('dateTo') as UntypedFormControl;
  }

  ngOnInit(): void {
    this.buildFormGroup();
    this.onEmployerChange();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  displayValue = (value: DictionaryItem): string | undefined => value?.Name;

  onSubmit() {
    if (this.statisticsWorkerAgreementsFormGroup.invalid) {
      return;
    }

    this.spinner.show();
    this.generate();
  }

  onBackward() {
    this.router.navigate(['/statistics']);
  }

  setSelectedEmployer(employer: DictionaryItem): void {
    if (employer) {
      this.employerObject.enable({ emitEvent: false });
      this.employerObject.reset(null, { emitEvent: false });
    }
  }

  public dateFromFilter = (date: Moment): boolean => !this.dateTo.value || date?.isSameOrBefore(this.dateTo.value);
  public dateToFilter = (date: Moment): boolean => !this.dateFrom.value || date.isSameOrAfter(this.dateFrom.value);

  private buildFormGroup() {
    this.statisticsWorkerAgreementsFormGroup = this.formBuilder.group({
      employer: [null, [Validators.required]],
      employerObject: [{ value: null, disabled: true }],
      dateFrom: [null],
      dateTo: [null],
    });
  }

  private generate() {
    this.statisticsService
      .getWorkerAgreementDurationStats(this.employer.value, this.employerObject.value?.Id, this.dateFrom.value, this.dateTo.value)
      .pipe(finalize(() => this.spinner.hide()))
      .subscribe((res: WorkerAgreementDurationStatsDto) => {
        const keys = Object.keys(res.WorkerAgreementElements);

        const dates = keys.map((date) => new Date(date)).sort((a, b) => a.getTime() - b.getTime());
        const labels = dates.map((date) => date.toISOString().split('T')[0]);
        const concludedCount = keys.map((date) => res.WorkerAgreementElements[date].ConcludedCount);
        const terminatedCount = keys.map((date) => res.WorkerAgreementElements[date].TerminatedCount);

        this.data.labels = labels;
        this.data.datasets[0].data = concludedCount;
        this.data.datasets[1].data = terminatedCount;
        this.chart.options.scales.y.min = Math.min(...concludedCount) - 10;
        this.chart.options.scales.y.max = Math.max(...concludedCount) + 10;
        this.chart?.update();

        this.concludedTotal = res.ConcludedTotal;
        this.terminatedTotal = res.TerminatedTotal;
      });
  }

  private onEmployerChange() {
    this.employer.statusChanges.pipe(debounceTime(this.timeBetweenInput), takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.employer.valid) {
        this.employerObject.enable({ emitEvent: false });
      } else {
        this.employerObject.reset(null, { emitEvent: false });
        this.employerObject.disable({ emitEvent: false });
      }
    });

    this.listOfEmployerObjects$ = this.employer.valueChanges.pipe(
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      takeUntil(this.unsubscribe$),
      switchMap((_) => {
        if (this.employer.value) {
          return this.dictionaryService.getEmployerObjectsByEmployerId(this.employer.value).pipe(
            tap((items) => {
              if (items.length === 1) {
                this.employerObject.enable();
              } else {
                this.employerObjectSelectRef?.nativeElement.focus();
                this.employerObjectSelect.openPanel();
              }
            }),
          );
        } else {
          return of([]);
        }
      }),
      switchMap((employerObjects) =>
        this.employerObject.valueChanges.pipe(
          startWith(undefined),
          debounceTime(this.timeBetweenInput),
          takeUntil(this.unsubscribe$),
          map((_) =>
            !this.employerObject.value?.toLowerCase || employerObjects === null || employerObjects.length === 1
              ? employerObjects
              : employerObjects.filter((item) => item.Name?.toLowerCase().includes(this.employerObject.value?.toLowerCase())),
          ),
          tap((employerObjects) => {
            const res = employerObjects.find(
              (employerObject) =>
                this.employerObject.value?.toLowerCase && employerObject.Name?.toLowerCase() === this.employerObject.value?.toLowerCase(),
            );

            if (res) {
              this.employerObject.setValue(res);
              this.employerObjectSelect.closePanel();
            }
          }),
        ),
      ),
    );
  }

  chartType: ChartType = 'line';
  data: ChartData<'line'> = {
    labels: [],
    datasets: [
      {
        label: this.translateService.instant('Statistics.WorkerAgreementConcluded'),
        data: [],
      },
      {
        label: this.translateService.instant('Statistics.WorkerAgreementTerminated'),
        data: [],
      },
    ],
  };
  options: ChartOptions<'line'> = {
    responsive: true,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
    plugins: {
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          drag: {
            enabled: true,
            borderColor: 'rgb(54, 162, 235)',
            borderWidth: 1,
            backgroundColor: 'rgba(54, 162, 235, 0.3)',
          },
          mode: 'xy',
        },
        pan: {
          enabled: true,
          mode: 'xy',
          modifierKey: 'shift',
        },
        limits: {
          x: { min: 0, max: 'original', minRange: 1 },
          y: { min: 0, max: 'original', minRange: 1 },
        },
      },
    },
  };
}
